import { useState, useCallback, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import type { TestItem } from '@/config/testItems';

const PERSIST_DEBOUNCE_MS = 500;

export interface TestEngineProps {
  testId: string;
  commercialName: string;
  items: TestItem[];
  /** Instrucción superior (opcional). Se muestra arriba del contenido. */
  instruction?: string;
  onComplete: (answers: Record<string, number>) => void;
  initialAnswers?: Record<string, number>;
  /** Llamado en cada cambio de respuesta para auto-guardado (Firebase, etc.) */
  onAnswerChange?: (itemId: string, value: number, allAnswers: Record<string, number>) => void;
  /** Si retorna true al presionar Siguiente, finaliza la prueba (ej. NOM35 early exit) */
  checkFinishCondition?: (
    currentAnswer: number,
    allAnswers: Record<string, number>,
    currentItemId: string
  ) => boolean;
  /** Clave para persistir en localStorage como respaldo ante recargas/cortes */
  persistKey?: string;
}

function loadFromStorage<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function saveToStorage(key: string, data: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch {
    // Ignorar errores de quota o privacidad
  }
}

export function TestEngine({
  testId,
  commercialName,
  items,
  instruction,
  onComplete,
  initialAnswers = {},
  onAnswerChange,
  checkFinishCondition,
  persistKey,
}: TestEngineProps) {
  const [answers, setAnswers] = useState<Record<string, number>>(() => {
    if (!persistKey) return initialAnswers;
    const stored = loadFromStorage<{ answers: Record<string, number>; currentIndex: number }>(
      persistKey
    );
    if (stored?.answers && Object.keys(stored.answers).length > 0) {
      return { ...initialAnswers, ...stored.answers };
    }
    return initialAnswers;
  });
  const [currentIndex, setCurrentIndex] = useState(() => {
    if (!persistKey) return 0;
    const stored = loadFromStorage<{ answers: Record<string, number>; currentIndex: number }>(
      persistKey
    );
    const mergedAnswers =
      stored?.answers && Object.keys(stored.answers).length > 0
        ? { ...initialAnswers, ...stored.answers }
        : initialAnswers;
    const firstUnansweredIndex = (() => {
      for (let i = 0; i < items.length; i++) {
        if (mergedAnswers[items[i]!.id] == null) return i;
      }
      return -1;
    })();
    if (stored?.currentIndex != null && stored.currentIndex >= 0 && stored.currentIndex < items.length) {
      // Evitar quedar "atorado" en la última pregunta si faltan respuestas previas (ej. refresh o storage).
      if (firstUnansweredIndex >= 0 && stored.currentIndex > firstUnansweredIndex) return firstUnansweredIndex;
      return stored.currentIndex;
    }
    return 0;
  });

  const firstUnansweredIndex = (() => {
    for (let i = 0; i < items.length; i++) {
      if (answers[items[i]!.id] == null) return i;
    }
    return -1;
  })();
  const onAnswerChangeRef = useRef(onAnswerChange);
  onAnswerChangeRef.current = onAnswerChange;
  const persistTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentIndexRef = useRef(currentIndex);
  currentIndexRef.current = currentIndex;

  const persistToStorage = useCallback(() => {
    if (!persistKey) return;
    saveToStorage(persistKey, { answers, currentIndex: currentIndexRef.current, testId });
  }, [persistKey, answers, testId]);

  useEffect(() => {
    if (!persistKey) return;
    persistToStorage();
  }, [persistKey, persistToStorage]);

  const setAnswer = useCallback(
    (itemId: string, value: number) => {
      setAnswers((prev) => {
        const next = { ...prev, [itemId]: value };
        onAnswerChangeRef.current?.(itemId, value, next);
        if (persistKey) {
          if (persistTimeoutRef.current) clearTimeout(persistTimeoutRef.current);
          persistTimeoutRef.current = setTimeout(() => {
            saveToStorage(persistKey, { answers: next, currentIndex: currentIndexRef.current, testId });
            persistTimeoutRef.current = null;
          }, PERSIST_DEBOUNCE_MS);
        }
        return next;
      });
    },
    [persistKey, testId]
  );

  useEffect(() => {
    return () => {
      if (persistTimeoutRef.current) clearTimeout(persistTimeoutRef.current);
    };
  }, []);

  const answered = items.filter((i) => answers[i.id] != null).length;
  const canSubmit = answered === items.length;

  const handleSubmit = () => {
    if (!canSubmit) return;
    if (persistKey) localStorage.removeItem(persistKey);
    onComplete(answers);
  };

  const handleNext = useCallback(() => {
    const item = items[currentIndex];
    if (!item) return;
    const currentAnswer = answers[item.id] ?? 0;
    const shouldFinish =
      checkFinishCondition?.(currentAnswer, answers, item.id) ?? false;
    if (shouldFinish) {
      if (persistKey) localStorage.removeItem(persistKey);
      onComplete(answers);
      return;
    }
    setCurrentIndex((i) => {
      const next = i + 1;
      if (persistKey) {
        saveToStorage(persistKey, { answers, currentIndex: next, testId });
      }
      return next;
    });
  }, [items, currentIndex, answers, checkFinishCondition, onComplete, persistKey, testId]);

  const handlePrev = useCallback(() => {
    setCurrentIndex((i) => {
      const next = Math.max(0, i - 1);
      if (persistKey) {
        saveToStorage(persistKey, { answers, currentIndex: next, testId });
      }
      return next;
    });
  }, [answers, persistKey, testId]);

  const item = items[currentIndex];
  if (!item) return null;

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="text-lg">{commercialName}</CardTitle>
        <p className="text-sm text-muted-foreground">
          Pregunta {currentIndex + 1} de {items.length}
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {instruction && (
          <p className="text-sm text-muted-foreground border-b pb-3">{instruction}</p>
        )}
        <p className="font-medium">{item.text}</p>
        <div className="flex flex-col gap-2">
          {item.options.map((opt) => (
            <label
              key={opt.value}
              className="flex cursor-pointer items-center gap-2 rounded-md border p-3 hover:bg-muted/50"
            >
              <input
                type="radio"
                name={item.id}
                checked={answers[item.id] === opt.value}
                onChange={() => setAnswer(item.id, opt.value)}
              />
              <span className="text-sm">{opt.label}</span>
            </label>
          ))}
        </div>
        <div className="flex justify-between">
          <Button
            type="button"
            variant="outline"
            disabled={currentIndex === 0}
            onClick={handlePrev}
          >
            Anterior
          </Button>
          {currentIndex < items.length - 1 ? (
            <Button
              type="button"
              onClick={handleNext}
              disabled={answers[item.id] == null}
            >
              Siguiente
            </Button>
          ) : (
            <Button type="button" onClick={handleSubmit} disabled={!canSubmit}>
              Finalizar prueba
            </Button>
          )}
        </div>
        {!canSubmit && currentIndex === items.length - 1 && (
          <div className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
            Faltan <span className="font-medium">{items.length - answered}</span> pregunta(s) por responder.
            {firstUnansweredIndex >= 0 && (
              <div className="mt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentIndex(firstUnansweredIndex)}
                >
                  Ir a la primera sin responder
                </Button>
              </div>
            )}
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          Respondidas: {answered} / {items.length}
        </p>
      </CardContent>
    </Card>
  );
}
